import React, { useMemo, useState } from 'react';

/**
 * What the vendor changed, for the approval reviewer.
 *
 * The old panel rendered every change as one flat <ul> built from a single audit
 * row, and recovered image URLs by string-splitting field names — which is why it
 * showed mangled URLs, raw ids and, often, nothing at all.
 *
 * The backend now sends `changes`: a real snapshot-vs-current diff, already
 * categorized, already labelled, with thumbnails and human names instead of
 * uuids. This renders that. When there is no baseline to diff against — products
 * that entered review before change tracking existed — it falls back to the old
 * flat list under an honest notice rather than rendering blank.
 */

// Products already pending when detailed tracking shipped have no baseline and
// can never get a truthful one, so their panel keeps the legacy list.
const LEGACY_REASON = 'legacy_pending_no_baseline';

const REASON_NOTICES = {
  new_product: {
    tone: 'info',
    icon: '🆕',
    text: 'New product — everything below is new content awaiting first approval.',
  },
  [LEGACY_REASON]: {
    tone: 'info',
    icon: 'ℹ️',
    text: 'Detailed change tracking started after this product entered review — showing the recorded change log instead.',
  },
  no_baseline_bulk_push: {
    tone: 'info',
    icon: 'ℹ️',
    text: 'Submitted for approval without content changes.',
  },
  unsupported_snapshot_version: {
    tone: 'warning',
    icon: '⚠️',
    text: 'Change details are unavailable for this product.',
  },
};

const formatCount = (n, singular, plural) =>
  `${n} ${n === 1 ? singular : plural || `${singular}s`}`;

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '(none)';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${formatCount(mins, 'minute')} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${formatCount(hours, 'hour')} ago`;
  return `${formatCount(Math.round(hours / 24), 'day')} ago`;
};

/** old → new, using the diff pills the rest of the admin already uses. */
const ValuePair = ({ oldValue, newValue }) => (
  <span className="changeValue">
    <span className="oldValue">{displayValue(oldValue)}</span>
    <span className="arrow">→</span>
    <span className="newValue">{displayValue(newValue)}</span>
  </span>
);

const FieldRow = ({ change, modifier = '' }) => {
  // Descriptions are HTML blobs; the backend sends a plain-text preview and a
  // length delta so the reviewer sees the gist instead of a wall of markup.
  if (change.kind === 'text_blob') {
    return (
      <li className={`changeItem changesTextBlob ${modifier}`}>
        <span className="fieldName">{change.label}</span>
        <span className="changeValue changesTextBlobBody">
          <span className="changesTextBlobMeta">
            {change.old_length} → {change.new_length} characters
          </span>
          <span className="changesPreviewPair">
            <span className="changesPreview changesPreviewOld">
              {change.old_preview || '(empty)'}
            </span>
            <span className="changesPreview changesPreviewNew">
              {change.new_preview || '(empty)'}
            </span>
          </span>
        </span>
      </li>
    );
  }

  if (change.kind === 'tags') {
    const added = change.added || [];
    const removed = change.removed || [];
    return (
      <li className={`changeItem ${modifier}`}>
        <span className="fieldName">{change.label}</span>
        <span className="changeValue changesTagList">
          {removed.map((tag) => (
            <span key={`r-${tag}`} className="changesTagChip changesTagRemoved">
              {tag}
            </span>
          ))}
          {added.map((tag) => (
            <span key={`a-${tag}`} className="changesTagChip changesTagAdded">
              {tag}
            </span>
          ))}
          {!added.length && !removed.length && (
            <ValuePair oldValue={change.old} newValue={change.new} />
          )}
        </span>
      </li>
    );
  }

  return (
    <li className={`changeItem ${modifier}`}>
      <span className="fieldName">{change.label}</span>
      <ValuePair oldValue={change.old} newValue={change.new} />
    </li>
  );
};

/** Sections collapse so a product with 40 variant edits stays scannable. */
const Section = ({ title, icon, count, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!count) return null;
  return (
    <div className={`changesSection ${open ? 'expanded' : ''}`}>
      <button
        type="button"
        className="changesSectionHeader"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="changesSectionTitle">
          {icon} {title}
        </span>
        <span className="changesSectionMeta">
          <span className="changesCountBadge">{count}</span>
          <span className="expandIcon">{open ? '▼' : '▶'}</span>
        </span>
      </button>
      {open && <div className="changesSectionBody">{children}</div>}
    </div>
  );
};

const MediaTile = ({ item, variant }) => (
  <div className={`changesMediaTile changesMedia-${variant}`}>
    <div className="imagePickerItem changesMediaThumb">
      {item.thumbnail_url ? (
        <img src={item.thumbnail_url} alt={item.alt_text || item.label} />
      ) : (
        <span className="changesMediaMissing">no preview</span>
      )}
    </div>
    <div className="changesMediaCaption" title={item.label}>
      {item.label}
    </div>
    {item.variants && item.variants.length > 0 && (
      <div className="changesMediaVariants">{item.variants.join(', ')}</div>
    )}
  </div>
);

const VariantBlock = ({ variant, tone }) => (
  <div className={`changesVariantBlock changesVariant-${tone}`}>
    <div className="changesVariantHeader">
      <strong>{variant.label}</strong>
      {variant.sku && <span className="changesSkuBadge">{variant.sku}</span>}
    </div>
    {variant.fields && variant.fields.length > 0 && (
      <ul className="changesFieldList">
        {variant.fields.map((field) => (
          <li key={field.field} className="changeItem variant-change">
            <span className="fieldName">{field.label}</span>
            {'value' in field ? (
              <span className="changeValue">
                <span className="newValue">{displayValue(field.value)}</span>
              </span>
            ) : (
              <ValuePair oldValue={field.old} newValue={field.new} />
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ChangesReviewPanel = ({ changes, legacyChanges = [], editors = [] }) => {
  const [logOpen, setLogOpen] = useState(false);

  const reason = changes?.reason || null;
  const notice = reason ? REASON_NOTICES[reason] : null;

  // Fall back to the flat legacy list only when there is genuinely no baseline
  // to diff. Everything else renders the structured view.
  const useLegacy = !changes || reason === LEGACY_REASON;

  const headline = useMemo(() => {
    const summary = changes?.summary || {};
    const parts = [];
    if (summary.product_fields) {
      parts.push(formatCount(summary.product_fields, 'product field'));
    }
    const variantTotal =
      (summary.variants_added || 0) +
      (summary.variants_removed || 0) +
      (summary.variants_modified || 0);
    if (variantTotal) parts.push(formatCount(variantTotal, 'variant'));
    const mediaTotal =
      (summary.media_added || 0) +
      (summary.media_removed || 0) +
      (summary.media_modified || 0) +
      (summary.media_reordered || 0);
    if (mediaTotal) parts.push(formatCount(mediaTotal, 'image'));
    if (summary.options_changed) parts.push('options');
    return parts.join(' · ');
  }, [changes]);

  const editedBy = editors.length
    ? `Edited by ${editors[0].name || editors[0].email}${
        editors.length > 1 ? ` +${editors.length - 1} more` : ''
      }${editors[0].at ? ` · ${relativeTime(editors[0].at)}` : ''}`
    : '';

  const legacyRows = Array.isArray(legacyChanges) ? legacyChanges : [];

  // Nothing to say and nothing to explain — render nothing rather than an empty box.
  if (!notice && !headline && (!useLegacy || legacyRows.length === 0)) {
    return null;
  }

  const variants = changes?.variants || { added: [], removed: [], modified: [] };
  const media = changes?.media || {
    added: [],
    removed: [],
    modified: [],
    reordered: { count: 0, items: [], summary: '' },
  };
  const options = changes?.options || { changed: false, added: [], removed: [], renamed: [] };

  return (
    <div className="changesReviewPanel">
      <div className="changesSummaryBar">
        <div className="changesSummaryMain">
          <h4>📝 Vendor Changes</h4>
          {headline && <span className="changesHeadline">{headline}</span>}
        </div>
        {editedBy && <span className="changesAttribution">{editedBy}</span>}
      </div>

      {notice && (
        <div className={`changesNotice changesNotice-${notice.tone}`}>
          <span className="changesNoticeIcon">{notice.icon}</span>
          <span>{notice.text}</span>
        </div>
      )}

      {useLegacy ? (
        legacyRows.length > 0 && (
          <ul className="changesLegacyList">
            {legacyRows.map((f, idx) => (
              <li key={`${f.field}-${idx}`} className="changeItem">
                <span className="fieldName">{f.field}</span>
                {f.action ? (
                  <span className="changeValue">
                    <span className="newValue">{f.message || f.action}</span>
                  </span>
                ) : (
                  <ValuePair oldValue={f.old} newValue={f.new} />
                )}
              </li>
            ))}
          </ul>
        )
      ) : (
        <>
          <Section title="Product Details" icon="📋" count={changes.product?.length || 0}>
            <ul className="changesFieldList">
              {(changes.product || []).map((change) => (
                <FieldRow key={change.field} change={change} />
              ))}
            </ul>
          </Section>

          <Section
            title="Variants"
            icon="🔀"
            count={
              variants.added.length + variants.removed.length + variants.modified.length
            }
            defaultOpen={
              variants.added.length + variants.removed.length + variants.modified.length <= 5
            }
          >
            {variants.added.length > 0 && (
              <div className="changesGroup">
                <div className="changesGroupLabel changesGroupAdded">
                  Added ({variants.added.length})
                </div>
                {variants.added.map((v, i) => (
                  <VariantBlock key={`a-${v.sku}-${i}`} variant={v} tone="added" />
                ))}
              </div>
            )}
            {variants.removed.length > 0 && (
              <div className="changesGroup">
                <div className="changesGroupLabel changesGroupRemoved">
                  Removed ({variants.removed.length})
                </div>
                {variants.removed.map((v, i) => (
                  <VariantBlock key={`r-${v.sku}-${i}`} variant={v} tone="removed" />
                ))}
              </div>
            )}
            {variants.modified.length > 0 && (
              <div className="changesGroup">
                <div className="changesGroupLabel changesGroupModified">
                  Updated ({variants.modified.length})
                </div>
                {variants.modified.map((v, i) => (
                  <VariantBlock key={`m-${v.sku}-${i}`} variant={v} tone="modified" />
                ))}
              </div>
            )}
          </Section>

          <Section
            title="Images"
            icon="🖼️"
            count={
              media.added.length +
              media.removed.length +
              media.modified.length +
              (media.reordered?.count || 0)
            }
          >
            {media.added.length > 0 && (
              <div className="changesGroup">
                <div className="changesGroupLabel changesGroupAdded">
                  Added ({media.added.length})
                </div>
                <div className="changesMediaGrid">
                  {media.added.map((m, i) => (
                    <MediaTile key={`a-${m.label}-${i}`} item={m} variant="added" />
                  ))}
                </div>
              </div>
            )}
            {media.removed.length > 0 && (
              <div className="changesGroup">
                <div className="changesGroupLabel changesGroupRemoved">
                  Removed ({media.removed.length})
                </div>
                <div className="changesMediaGrid">
                  {media.removed.map((m, i) => (
                    <MediaTile key={`r-${m.label}-${i}`} item={m} variant="removed" />
                  ))}
                </div>
              </div>
            )}
            {media.modified.length > 0 && (
              <div className="changesGroup">
                <div className="changesGroupLabel changesGroupModified">
                  Updated ({media.modified.length})
                </div>
                {media.modified.map((m, i) => (
                  <div key={`m-${m.label}-${i}`} className="changesMediaModified">
                    <MediaTile item={m} variant="modified" />
                    <ul className="changesFieldList">
                      {(m.fields || []).map((field) => (
                        <FieldRow key={field.field} change={field} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {(media.reordered?.count || 0) > 0 && (
              <div className="changesGroup">
                <div className="changesGroupLabel">{media.reordered.summary}</div>
                <div className="changesReorderStrip">
                  {media.reordered.items.map((item, i) => (
                    <div key={`o-${item.label}-${i}`} className="changesReorderItem">
                      <div className="imagePickerItem changesMediaThumb changesReorderThumb">
                        {item.thumbnail_url ? (
                          <img src={item.thumbnail_url} alt={item.label} />
                        ) : (
                          <span className="changesMediaMissing">?</span>
                        )}
                      </div>
                      <span className="changesReorderPositions">
                        #{(item.old_position ?? 0) + 1} → #{(item.new_position ?? 0) + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          <Section title="Options" icon="⚙️" count={options.changed ? 1 : 0}>
            <ul className="changesFieldList">
              {options.renamed.map((r, i) => (
                <li key={`ren-${i}`} className="changeItem">
                  <span className="fieldName">Renamed</span>
                  <ValuePair oldValue={r.old} newValue={r.new} />
                </li>
              ))}
              {options.added.map((name) => (
                <li key={`add-${name}`} className="changeItem added">
                  <span className="fieldName">Added</span>
                  <span className="changeValue">
                    <span className="newValue">{name}</span>
                  </span>
                </li>
              ))}
              {options.removed.map((name) => (
                <li key={`rem-${name}`} className="changeItem removed">
                  <span className="fieldName">Removed</span>
                  <span className="changeValue">
                    <span className="oldValue">{name}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </>
      )}

      {/* Provenance for anyone who needs it, out of the way for everyone else. */}
      {legacyRows.length > 0 && !useLegacy && (
        <div className={`auditEntry changesChangeLog ${logOpen ? 'expanded' : ''}`}>
          <button
            type="button"
            className="auditEntryHeader clickable changesChangeLogHeader"
            onClick={() => setLogOpen((v) => !v)}
            aria-expanded={logOpen}
          >
            <span>🕓 Change log ({legacyRows.length})</span>
            <span className="expandIcon">{logOpen ? '▼' : '▶'}</span>
          </button>
          {logOpen && (
            <div className="auditEntryDetails">
              <ul className="changesFieldList">
                {legacyRows.map((f, idx) => (
                  <li key={`log-${f.field}-${idx}`} className="changeItem">
                    <span className="fieldName">{f.field}</span>
                    {f.action ? (
                      <span className="changeValue">
                        <span className="newValue">{f.message || f.action}</span>
                      </span>
                    ) : (
                      <ValuePair oldValue={f.old} newValue={f.new} />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChangesReviewPanel;
